import { Canvas, useLoader, ThreeEvent } from '@react-three/fiber'
import React, { useEffect, useRef, useState } from 'react'
import { TextureLoader } from 'three/src/loaders/TextureLoader'
import { OrbitControls, SoftShadows } from '@react-three/drei'
import { motion } from 'framer-motion-3d'
import * as THREE from 'three'
import { CubeProps, ItemType, MeshProps } from '../Cube/types'
import CountdownTile from './CountdownTile'
import KandiTile from './KandiTile'

export const initSpinDuration = 2

const Mesh: React.FC<MeshProps> = ({ items, onFaceClick }) => {
  const mesh = useRef(null)
  const [initSpinDone, setInitSpinDone] = useState(false)
  SoftShadows({ samples: 100, size: 100 })

  const isDraggingRef = useRef(false)
  const lastPosRef = useRef<{ x: number; y: number } | null>(null)
  const [hasDragged, setHasDragged] = useState(false)

  const handlePointerMove = (e: PointerEvent) => {
    if (isDraggingRef.current && lastPosRef.current && mesh.current) {
      const dx = e.clientX - lastPosRef.current.x
      const dy = e.clientY - lastPosRef.current.y
      mesh.current.rotation.y += dx * 0.01
      mesh.current.rotation.x += dy * 0.01
      lastPosRef.current = { x: e.clientX, y: e.clientY }

      // Limit rotations to [0, 2π)
      mesh.current.rotation.x = THREE.MathUtils.euclideanModulo(mesh.current.rotation.x, Math.PI * 2)
      mesh.current.rotation.y = THREE.MathUtils.euclideanModulo(mesh.current.rotation.y, Math.PI * 2)
      mesh.current.rotation.z = THREE.MathUtils.euclideanModulo(mesh.current.rotation.z, Math.PI * 2)

      if (!hasDragged) {
        setHasDragged(true)
      }
    }
  }

  const handlePointerUp = () => {
    isDraggingRef.current = false
    window.removeEventListener('pointermove', handlePointerMove)
    window.removeEventListener('pointerup', handlePointerUp)
  }

  const handlePointerDown = (e: ThreeEvent<PointerEvent>) => {
    e.stopPropagation()
    if (initSpinDone) {
      isDraggingRef.current = true
      lastPosRef.current = { x: e.clientX, y: e.clientY }
      window.addEventListener('pointermove', handlePointerMove)
      window.addEventListener('pointerup', handlePointerUp)
    }
  }

  useEffect(() => {
    const spinCube = () => {
      if (!mesh.current) return
      const initialRotation = { x: 0, y: 0 }
      const targetRotation = { x: Math.PI * 4.05, y: Math.PI * 1.4 }
      const duration = initSpinDuration
      const startTime = performance.now()

      const animate = (time: number) => {
        const elapsed = (time - startTime) / 1000
        const progress = Math.min(elapsed / duration, 1)
        const easing = (t: number) => t * (2 - t)

        if (mesh.current) {
          mesh.current.rotation.x = initialRotation.x + (targetRotation.x - initialRotation.x) * easing(progress)
          mesh.current.rotation.y = initialRotation.y + (targetRotation.y - initialRotation.y) * easing(progress)
        }

        if (progress < 1) {
          requestAnimationFrame(animate)
        }
      }

      requestAnimationFrame(animate)
    }

    spinCube()
    setTimeout(() => {
      setInitSpinDone(true)
    }, initSpinDuration * 1000 - 200)
  }, [])

  // Load images for faces that have src
  const imagesSrc = items.map((item) => item.src || null).filter(Boolean) as string[]
  const loadedImages = useLoader(TextureLoader, imagesSrc.length > 0 ? imagesSrc : [''])
  // We have a mismatch in length if some items don't have src
  // We'll map the images carefully.

  const materials = items.map((item, index) => {
    return <meshStandardMaterial key={index} map={loadedImages[index]} attach={`material-${index}`} />
  })

  const faceTransforms = [
    { position: [0, 0, 0.51], rotation: [0, 0, 0] }, // Front
    { position: [0.51, 0, 0], rotation: [0, Math.PI / 2, 0] }, // Right
    { position: [0, 0, -0.51], rotation: [0, Math.PI, 0] }, // Back
    { position: [-0.51, 0, 0], rotation: [0, -Math.PI / 2, 0] }, // Left
    { position: [0, 0.51, 0], rotation: [-Math.PI / 2, 0, 0] }, // Top
    { position: [0, -0.51, 0], rotation: [Math.PI / 2, 0, 0] }, // Bottom
  ]

  return (
    <>
      <motion.mesh
        ref={mesh}
        castShadow
        receiveShadow
        onClick={(event) => {
          if (event.delta > 10) return
          const faceIndex = Math.floor(event.faceIndex / 2)
          onFaceClick(faceIndex)
        }}
        onPointerDown={handlePointerDown}
      >
        <boxGeometry args={[1, 1, 1]} />
        {materials}
        {items.map((item, index) => {
          if (item.type === ItemType.COUNTDOWN) {
            const { position } = faceTransforms[index]
            return (
              <>
                <CountdownTile position={position as unknown as THREE.Vector3} targetDate={item.targetDate} />
              </>
            )
          } else if (item.type === ItemType.KANDI) {
            return (
              <>
                <KandiTile item={item} />
              </>
            )
          }
          return null
        })}
      </motion.mesh>
    </>
  )
}

const Cube: React.FC<CubeProps> = ({ items }) => {
  const handelFaceClick = (index: number) => {
    if (!!items[index].link) {
      if (items[index].openExternal === true) {
        window.open(items[index].link, '_blank')
      } else {
        window.open(items[index].link, '_self')
      }
    }
  }

  if (!Array.isArray(items)) return null
  return (
    <Canvas shadows camera={{ position: [0, 1, 3], fov: 50 }}>
      <OrbitControls enableZoom={false} enablePan={false} enableRotate={false} />
      <directionalLight
        position={[0, 3.5, 5]}
        intensity={1}
        castShadow
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
        shadow-camera-left={-5}
        shadow-camera-right={5}
        shadow-camera-top={10}
        shadow-camera-bottom={-100}
        shadow-camera-near={0.1}
        shadow-camera-far={100}
      />
      <ambientLight intensity={1} />

      <Mesh items={items} onFaceClick={handelFaceClick} />

      <mesh receiveShadow rotation-x={-Math.PI / 2} position={[0, -1.5, 0]}>
        <planeGeometry args={[50, 50]} />
        <shadowMaterial opacity={0.5} />
      </mesh>
    </Canvas>
  )
}

export default Cube
